import { useState, useCallback } from 'react';
import { arrangePanelRects } from '../utils/panelLayout';
import { normalizeChannelPanelConfigs } from '../utils/channelPanelConfigs';

const STORAGE_KEY = 'trackAnalysis_tabs';
const OLD_LAYOUT_KEY = 'trackAnalysis_windowLayout';

const DEFAULT_VISIBILITY = {
  showMap: true,
  showSpeedTrace: true,
  showGTrace: false,
  showDeltaTrace: false,
  showVideo: false,
  showMetrics: false,
  showPromptPanel: false,
  showFrictionCircle: false,
  showScatterPlot: false,
  showComments: false,
  showSplits: false,
  showPowerTrace: false,
  showHeadToHead: false,
  showTrackDifference: false,
  showLap3D: false,
};

const LAYOUT_DEFAULTS = {
  speed:          { x: 200, y: 90,  width: 860, height: 280 },
  map:            { x: 200, y: 390, width: 680, height: 460 },
  video:          { x: 900, y: 90,  width: 500, height: 340 },
  metrics:        { x: 900, y: 450, width: 460, height: 520 },
  prompt:         { x: 200, y: 870, width: 540, height: 420 },
  gTrace:         { x: 200, y: 390, width: 860, height: 200 },
  delta:          { x: 200, y: 610, width: 860, height: 180 },
  frictionCircle: { x: 200, y: 90,  width: 500, height: 460 },
  scatterPlot:    { x: 720, y: 90,  width: 520, height: 460 },
  comments:       { x: 1080, y: 90, width: 340, height: 480 },
  splits:         { x: 200, y: 390, width: 860, height: 300 },
  power:          { x: 200, y: 610, width: 860, height: 220 },
  readouts:       { x: 1080, y: 610, width: 320, height: 280 },
  headtohead:     { x: 260, y: 130, width: 800, height: 560 },
  trackDifference:{ x: 240, y: 120, width: 900, height: 600 },
  // Bigger than the rest by default: it is a camera view, and the ghost race is
  // unreadable in a letterbox.
  lap3d:          { x: 240, y: 120, width: 900, height: 600 },
};

// Cascade new panels diagonally so they don't stack exactly on top of each
// other, but wrap the offset (rather than growing it forever) so the Nth
// channel/formula panel doesn't cascade off the bottom of the viewport.
// x starts past the 180px lap-selector sidebar (matching LAYOUT_DEFAULTS'
// other entries) so a fresh panel never opens underneath it.
const CASCADE_WRAP = 10;

function channelDefault(index) {
  const i = index % CASCADE_WRAP;
  return { x: 200 + i * 24, y: 90 + i * 36, width: 860, height: 180 };
}

export function formulaDefault(index) {
  const i = index % CASCADE_WRAP;
  return { x: 224 + i * 24, y: 126 + i * 36, width: 860, height: 180 };
}

function makeTab(label, order, visibility = {}, windowLayout = {}, selectedChannelNames = [], valueChannelNames = [], channelPanelConfigs = {}) {
  return {
    id: crypto.randomUUID(),
    label,
    order,
    panelVisibility: { ...DEFAULT_VISIBILITY, ...visibility },
    windowLayout: { ...LAYOUT_DEFAULTS, ...windowLayout },
    selectedChannelNames,
    valueChannelNames,
    channelPanelConfigs: normalizeChannelPanelConfigs(channelPanelConfigs),
  };
}

function persist(data) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(data)); } catch {}
}

function loadInitialState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const data = JSON.parse(raw);
      if (data.tabs?.length > 0) return {
        ...data,
        tabs: data.tabs.map(tab => ({
          ...tab,
          channelPanelConfigs: normalizeChannelPanelConfigs(tab.channelPanelConfigs),
        })),
      };
    }
  } catch {}

  // Migrate existing single-layout key into a Default tab
  let migratedLayout = {};
  try {
    const old = localStorage.getItem(OLD_LAYOUT_KEY);
    if (old) migratedLayout = JSON.parse(old);
  } catch {}

  const defaultTab = makeTab('Default', 0, {}, migratedLayout);
  return { activeTabId: defaultTab.id, tabs: [defaultTab] };
}

export function useTabManager() {
  const [state, setState] = useState(loadInitialState);

  const activeTab = state.tabs.find(t => t.id === state.activeTabId) ?? state.tabs[0];
  // Merge defaults at read time so tabs persisted before a panel existed still
  // get a sane layout/visibility entry for it (e.g. headtohead).
  const winLayout = { ...LAYOUT_DEFAULTS, ...activeTab.windowLayout };
  const panelVisibility = { ...DEFAULT_VISIBILITY, ...activeTab.panelVisibility };
  const selectedChannelNames = activeTab.selectedChannelNames ?? [];
  const valueChannelNames = activeTab.valueChannelNames ?? [];
  const channelPanelConfigs = activeTab.channelPanelConfigs ?? {};

  // ── Layout ────────────────────────────────────────────────────────────────

  const updateWindow = useCallback((id, rect) => {
    setState(prev => {
      const tabs = prev.tabs.map(t =>
        t.id !== prev.activeTabId ? t : {
          ...t,
          windowLayout: { ...t.windowLayout, [id]: { ...(t.windowLayout[id] ?? {}), ...rect } },
        }
      );
      const next = { ...prev, tabs };
      persist(next);
      return next;
    });
  }, []);

  const getChannelLayout = useCallback((channelName, index) => {
    return activeTab.windowLayout[`ch_${channelName}`] ?? channelDefault(index);
  }, [activeTab]);

  const resetLayout = useCallback((preset = 'standard', workspaceBounds = null) => {
    const pad = 4;

    // Preset windows use viewport coordinates. Measure the actual panel area
    // when available so global/session/tab header height changes cannot push
    // them underneath the analysis toolbar. The fallback covers the current
    // desktop shell before its ref is mounted.
    const workspaceLeft   = workspaceBounds?.left   ?? 180;
    const workspaceTop    = workspaceBounds?.top    ?? 135;
    const workspaceWidth  = workspaceBounds?.width  ?? window.innerWidth - workspaceLeft;
    const workspaceHeight = workspaceBounds?.height ?? window.innerHeight - workspaceTop;

    const left   = Math.round(workspaceLeft) + pad;
    const top    = Math.round(workspaceTop)  + pad;
    const availW = Math.max(1, Math.round(workspaceWidth)  - pad * 2);
    const availH = Math.max(1, Math.round(workspaceHeight) - pad * 2);

    const leftW  = Math.round(availW * 0.65) - pad;
    const rightW = availW - leftW - pad;
    const standardLeftW  = Math.round(availW * 0.55) - pad;
    const standardRightW = availW - standardLeftW - pad;
    const speedH = Math.round(availH * 0.55) - pad;
    const lowerH = availH - speedH - pad;
    const mapH   = Math.round(availH * 0.55) - pad;
    const fcH    = availH - mapH - pad;

    // Video layouts: size right column so the video panel is exactly 16:9,
    // then give the remaining height to the map.
    const vidLeftW  = Math.round(availW * 0.50) - pad;
    const vidRightW = availW - vidLeftW - pad;
    const vidH      = Math.round(vidRightW * 9 / 16);
    const vidMapH   = availH - vidH - pad;

    const speed          = { x: left,                  y: top,                width: leftW,     height: speedH  };
    const gTrace         = { x: left,                  y: top + speedH + pad, width: leftW,     height: lowerH  };
    const delta          = { x: left,                  y: top + speedH + pad, width: leftW,     height: lowerH  };
    const map            = { x: left + leftW + pad,    y: top,                width: rightW,    height: availH  };
    const mapTop         = { x: left + leftW + pad,    y: top,                width: rightW,    height: mapH    };
    const frictionCircle = { x: left + leftW + pad,    y: top + mapH + pad,   width: rightW,    height: fcH     };
    const lap3dH         = Math.min(
      Math.max(1, Math.round(availH * 0.60) - pad),
      Math.max(1, availH - pad - 120),
    );
    const standardSpeed  = { x: left,                          y: top,                width: standardLeftW,  height: speedH };
    const standardG     = { x: left,                          y: top + speedH + pad, width: standardLeftW,  height: lowerH };
    const standardDelta = { x: left,                          y: top + speedH + pad, width: standardLeftW,  height: lowerH };
    const lap3d         = { x: left + standardLeftW + pad,    y: top,                width: standardRightW, height: lap3dH };
    const mapBelow3d    = { x: left + standardLeftW + pad,    y: top + lap3dH + pad, width: standardRightW, height: availH - lap3dH - pad };

    // Video preset panel positions
    const vidSpeed  = { x: left,                     y: top,               width: vidLeftW,  height: speedH  };
    const vidGTrace = { x: left,                     y: top + speedH + pad,width: vidLeftW,  height: lowerH  };
    const vidDelta  = { x: left,                     y: top + speedH + pad,width: vidLeftW,  height: lowerH  };
    const vidVideo  = { x: left + vidLeftW + pad,    y: top,               width: vidRightW, height: vidH    };
    const vidMap    = { x: left + vidLeftW + pad,    y: top + vidH + pad,  width: vidRightW, height: vidMapH };

    const presets = {
      standard: {
        windowLayout: { ...LAYOUT_DEFAULTS, speed: standardSpeed, gTrace: standardG, lap3d, map: mapBelow3d },
        panelVisibility: { ...DEFAULT_VISIBILITY, showSpeedTrace: true, showGTrace: true, showMap: true, showLap3D: true },
      },
      delta: {
        windowLayout: { ...LAYOUT_DEFAULTS, speed: standardSpeed, delta: standardDelta, lap3d, map: mapBelow3d },
        panelVisibility: { ...DEFAULT_VISIBILITY, showSpeedTrace: true, showDeltaTrace: true, showMap: true, showLap3D: true },
      },
      friction: {
        windowLayout: { ...LAYOUT_DEFAULTS, speed, delta, map: mapTop, frictionCircle },
        panelVisibility: { ...DEFAULT_VISIBILITY, showSpeedTrace: true, showDeltaTrace: true, showMap: true, showFrictionCircle: true },
      },
      videoStandard: {
        windowLayout: { ...LAYOUT_DEFAULTS, speed: vidSpeed, gTrace: vidGTrace, video: vidVideo, map: vidMap },
        panelVisibility: { ...DEFAULT_VISIBILITY, showSpeedTrace: true, showGTrace: true, showVideo: true, showMap: true },
      },
      videoDelta: {
        windowLayout: { ...LAYOUT_DEFAULTS, speed: vidSpeed, delta: vidDelta, video: vidVideo, map: vidMap },
        panelVisibility: { ...DEFAULT_VISIBILITY, showSpeedTrace: true, showDeltaTrace: true, showVideo: true, showMap: true },
      },
    };

    const chosen = presets[preset] ?? presets.standard;

    setState(prev => {
      const tabs = prev.tabs.map(t =>
        t.id !== prev.activeTabId ? t : { ...t, ...chosen }
      );
      const next = { ...prev, tabs };
      persist(next);
      return next;
    });
  }, []);

  // Tile the panels that are open right now without changing visibility. This
  // is deliberately separate from presets: it is the recovery path for a
  // workspace where a panel was covered, moved off-screen, or left at an
  // awkward size after several panels were opened and closed.
  const arrangeLayout = useCallback((panelIds, workspaceBounds = null) => {
    const ids = [...new Set((panelIds || []).filter(Boolean))];
    if (!ids.length) return;

    const workspaceLeft   = workspaceBounds?.left   ?? 180;
    const workspaceTop    = workspaceBounds?.top    ?? 135;
    const workspaceWidth  = workspaceBounds?.width  ?? window.innerWidth - workspaceLeft;
    const workspaceHeight = workspaceBounds?.height ?? window.innerHeight - workspaceTop;
    const arranged = arrangePanelRects(ids, {
      left: workspaceLeft,
      top: workspaceTop,
      width: workspaceWidth,
      height: workspaceHeight,
    });

    setState(prev => {
      const tabs = prev.tabs.map(t =>
        t.id !== prev.activeTabId ? t : {
          ...t,
          windowLayout: { ...t.windowLayout, ...arranged },
        }
      );
      const next = { ...prev, tabs };
      persist(next);
      return next;
    });
  }, []);

  const applyServerLayout = useCallback((serverLayout) => {
    if (!serverLayout || typeof serverLayout !== 'object') return;
    setState(prev => {
      const tabs = prev.tabs.map(t =>
        t.id !== prev.activeTabId ? t : {
          ...t,
          windowLayout: { ...t.windowLayout, ...serverLayout },
        }
      );
      const next = { ...prev, tabs };
      persist(next);
      return next;
    });
  }, []);

  // ── Visibility ────────────────────────────────────────────────────────────

  const updatePanelVisibility = useCallback((updates) => {
    setState(prev => {
      const tabs = prev.tabs.map(t =>
        t.id !== prev.activeTabId ? t : {
          ...t,
          panelVisibility: { ...t.panelVisibility, ...updates },
        }
      );
      const next = { ...prev, tabs };
      persist(next);
      return next;
    });
  }, []);

  const applyServerVisibility = useCallback((v) => {
    if (!v || typeof v !== 'object') return;
    setState(prev => {
      const tabs = prev.tabs.map(t =>
        t.id !== prev.activeTabId ? t : {
          ...t,
          panelVisibility: { ...t.panelVisibility, ...v },
        }
      );
      const next = { ...prev, tabs };
      persist(next);
      return next;
    });
  }, []);

  // ── Selected channels ─────────────────────────────────────────────────────

  const updateSelectedChannelNames = useCallback((names) => {
    setState(prev => {
      const tabs = prev.tabs.map(t =>
        t.id !== prev.activeTabId ? t : { ...t, selectedChannelNames: names }
      );
      const next = { ...prev, tabs };
      persist(next);
      return next;
    });
  }, []);

  const applyServerChannels = useCallback((names) => {
    if (!Array.isArray(names)) return;
    setState(prev => {
      const tabs = prev.tabs.map(t =>
        t.id !== prev.activeTabId ? t : { ...t, selectedChannelNames: names }
      );
      const next = { ...prev, tabs };
      persist(next);
      return next;
    });
  }, []);

  // ── Value channels (Readouts panel) ───────────────────────────────────────

  const updateValueChannelNames = useCallback((names) => {
    setState(prev => {
      const tabs = prev.tabs.map(t =>
        t.id !== prev.activeTabId ? t : { ...t, valueChannelNames: names }
      );
      const next = { ...prev, tabs };
      persist(next);
      return next;
    });
  }, []);

  const applyServerValueChannels = useCallback((names) => {
    if (!Array.isArray(names)) return;
    setState(prev => {
      const tabs = prev.tabs.map(t =>
        t.id !== prev.activeTabId ? t : { ...t, valueChannelNames: names }
      );
      const next = { ...prev, tabs };
      persist(next);
      return next;
    });
  }, []);

  // ── Per-generic-panel channel compositions ───────────────────────────────

  const updateChannelPanelConfigs = useCallback((configs) => {
    const normalized = normalizeChannelPanelConfigs(configs);
    setState(prev => {
      const tabs = prev.tabs.map(t =>
        t.id !== prev.activeTabId ? t : { ...t, channelPanelConfigs: normalized }
      );
      const next = { ...prev, tabs };
      persist(next);
      return next;
    });
  }, []);

  const applyServerChannelPanelConfigs = useCallback((configs) => {
    if (!configs || typeof configs !== 'object' || Array.isArray(configs)) return;
    const normalized = normalizeChannelPanelConfigs(configs);
    setState(prev => {
      const tabs = prev.tabs.map(t =>
        t.id !== prev.activeTabId ? t : { ...t, channelPanelConfigs: normalized }
      );
      const next = { ...prev, tabs };
      persist(next);
      return next;
    });
  }, []);

  // ── Tab CRUD ──────────────────────────────────────────────────────────────

  const createTab = useCallback((label) => {
    setState(prev => {
      const maxOrder = prev.tabs.reduce((m, t) => Math.max(m, t.order), -1);
      const newTab = makeTab(label ?? `Tab ${prev.tabs.length + 1}`, maxOrder + 1, { showMap: false });
      const tabs = [...prev.tabs, newTab];
      const next = { ...prev, tabs, activeTabId: newTab.id };
      persist(next);
      return next;
    });
  }, []);

  const renameTab = useCallback((id, label) => {
    setState(prev => {
      const tabs = prev.tabs.map(t => t.id === id ? { ...t, label } : t);
      const next = { ...prev, tabs };
      persist(next);
      return next;
    });
  }, []);

  const deleteTab = useCallback((id) => {
    setState(prev => {
      if (prev.tabs.length <= 1) return prev;
      const tabs = prev.tabs.filter(t => t.id !== id);
      let activeTabId = prev.activeTabId;
      if (activeTabId === id) {
        const sorted = [...prev.tabs].sort((a, b) => a.order - b.order);
        const idx = sorted.findIndex(t => t.id === id);
        activeTabId = (sorted[idx + 1] ?? sorted[idx - 1]).id;
      }
      const next = { ...prev, tabs, activeTabId };
      persist(next);
      return next;
    });
  }, []);

  const switchTab = useCallback((id) => {
    setState(prev => {
      if (prev.activeTabId === id) return prev;
      const next = { ...prev, activeTabId: id };
      persist(next);
      return next;
    });
  }, []);

  return {
    tabs: state.tabs,
    activeTabId: state.activeTabId,
    activeTab,
    winLayout,
    updateWindow,
    getChannelLayout,
    resetLayout,
    arrangeLayout,
    applyServerLayout,
    panelVisibility,
    updatePanelVisibility,
    applyServerVisibility,
    selectedChannelNames,
    updateSelectedChannelNames,
    applyServerChannels,
    valueChannelNames,
    updateValueChannelNames,
    applyServerValueChannels,
    channelPanelConfigs,
    updateChannelPanelConfigs,
    applyServerChannelPanelConfigs,
    createTab,
    renameTab,
    deleteTab,
    switchTab,
  };
}
